import { SxProps, Theme, Typography, useTheme, useMediaQuery } from '@mui/material';
import React, { FC, useMemo, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { MessageType } from 'src/constants/types';
import { getSmsPartsCount, MAX_SMS_PARTS } from 'src/helpers/helpers';
import { getApplication } from 'src/modules/applications/slices';
import { Application } from 'src/modules/applications/types';

type MessageCreditEstimatesProps = {
  viewType: "campaign" | "inbox";
  messageType: MessageType;
  messageText: string | undefined | null;
  hasAttachments: boolean;
  sx?: SxProps<Theme>;
  onValidationChange?: (isValid: boolean) => void;
};

export const MessageCreditEstimates: FC<MessageCreditEstimatesProps> = (props) => {
  const { messageType, messageText, hasAttachments, sx, onValidationChange } = props;
  const application = useSelector(getApplication) as Application;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const { credits, segments, isValid } = useMemo(() => {
    const segments = getSmsPartsCount(messageType, messageText, hasAttachments);
    const isValid = segments <= MAX_SMS_PARTS;

    let credits = 0;
    if (messageType === "sms") {
      credits = segments * application.priceForSMS;
    } else if (messageType === "mms") {
      credits = segments * application.priceForMMS;
    }

    return { credits, segments, isValid };
  }, [messageType, messageText, hasAttachments, application.priceForSMS, application.priceForMMS]);

  useEffect(() => {
    onValidationChange?.(segments <= MAX_SMS_PARTS);
  }, [segments]);

  return (
    <Typography 
      variant="body2" 
      sx={{
        ...sx,
        fontSize: isMobile ? '0.75rem' : 'inherit',
        color: isValid ? theme.palette.text.disabled : theme.palette.error.main,
      }}
    >
      {isValid ? `Estimated Credits: ${credits}` : `${segments} segment parts - exceeds ${MAX_SMS_PARTS} part maximum`}
    </Typography>
  );
};
